import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, FileText, ToggleLeft, ToggleRight, X, AlertCircle } from 'lucide-react';
import { apiClient, getApiErrorMessage } from '../../api-wa';
import Toast from './Toast';

const AutoReplyTab = () => {
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [toast, setToast] = useState(null);

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
    const [selectedRuleId, setSelectedRuleId] = useState(null);

    // Form inputs
    const [keywords, setKeywords] = useState('');
    const [replyText, setReplyText] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [existingMediaUrl, setExistingMediaUrl] = useState(null);
    const [buttons, setButtons] = useState([]);
    const [isActive, setIsActive] = useState(true);

    const fetchRules = async () => {
        setLoading(true);
        try {
            const response = await apiClient.get('/autoreplies/');
            setRules(response.data || []);
        } catch (err) {
            setError(getApiErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRules();
    }, []);

    const handleOpenCreateModal = () => {
        setModalMode('create');
        setSelectedRuleId(null);
        setKeywords('');
        setReplyText('');
        setSelectedFile(null);
        setExistingMediaUrl(null);
        setButtons([]);
        setIsActive(true);
        setShowModal(true);
    };

    const handleOpenEditModal = (rule) => {
        setModalMode('edit');
        setSelectedRuleId(rule.id);
        setKeywords(rule.keywords || '');
        setReplyText(rule.reply_text || '');
        setSelectedFile(null);
        setExistingMediaUrl(rule.media || null);
        setButtons(Array.isArray(rule.buttons) ? rule.buttons : []);
        setIsActive(rule.is_active !== false);
        setShowModal(true);
    };

    const handleAddButton = () => {
        if (buttons.length >= 10) {
            setToast({ message: 'You can add a maximum of 10 buttons.', type: 'error' });
            return;
        }
        setButtons([...buttons, '']);
    };

    const handleButtonChange = (index, value) => {
        const nextButtons = [...buttons];
        nextButtons[index] = value;
        setButtons(nextButtons);
    };

    const handleRemoveButton = (index) => {
        const nextButtons = buttons.filter((_, i) => i !== index);
        setButtons(nextButtons);
    };

    const handleToggleStatus = async (rule) => {
        try {
            await apiClient.patch(`/autoreplies/${rule.id}/`, {
                is_active: !rule.is_active
            });
            fetchRules();
            setToast({ message: `Rule status updated successfully!`, type: 'success' });
        } catch (err) {
            setToast({ message: 'Failed to update rule status.', type: 'error' });
        }
    };

    const handleDeleteRule = async (ruleId) => {
        if (!window.confirm('Are you sure you want to delete this auto-reply rule?')) return;
        try {
            await apiClient.delete(`/autoreplies/${ruleId}/`);
            fetchRules();
            setToast({ message: 'Auto-reply rule deleted successfully!', type: 'success' });
        } catch (err) {
            setToast({ message: 'Failed to delete auto-reply rule.', type: 'error' });
        }
    };

    const handleSaveRule = async (e) => {
        e.preventDefault();
        if (!keywords.trim()) {
            setToast({ message: 'Keywords are required.', type: 'error' });
            return;
        }
        if (!replyText.trim()) {
            setToast({ message: 'Reply message content is required.', type: 'error' });
            return;
        }

        const formData = new FormData();
        formData.append('keywords', keywords);
        formData.append('reply_text', replyText);
        formData.append('is_active', isActive);

        // Filter and serialize buttons
        const cleanButtons = buttons.map(b => b.trim()).filter(Boolean);
        formData.append('buttons', JSON.stringify(cleanButtons));

        if (selectedFile) {
            formData.append('media', selectedFile);
        }

        try {
            if (modalMode === 'create') {
                await apiClient.post('/autoreplies/', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                setToast({ message: 'Auto-reply rule created successfully!', type: 'success' });
            } else {
                await apiClient.patch(`/autoreplies/${selectedRuleId}/`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                setToast({ message: 'Auto-reply rule updated successfully!', type: 'success' });
            }
            setShowModal(false);
            fetchRules();
        } catch (err) {
            setToast({ message: getApiErrorMessage(err), type: 'error' });
        }
    };

    return (
        <div style={{ padding: '20px', background: '#fff', borderRadius: '16px', border: '1px solid #f1f5f9', marginTop: '24px' }}>
            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1e293b' }}>Automatic Keyword Replies</h3>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748b' }}>
                        Configure custom responses triggered automatically when candidates send matching keywords or phrases.
                    </p>
                </div>
                <button
                    onClick={handleOpenCreateModal}
                    style={{
                        background: 'var(--brand-purple, #7c3aed)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '12px',
                        padding: '10px 16px',
                        fontSize: '14px',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        boxShadow: '0 4px 6px -1px rgba(124, 58, 237, 0.1)'
                    }}
                >
                    <Plus size={16} /> Add Auto Reply
                </button>
            </div>

            {loading && <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading rules...</div>}

            {!loading && rules.length === 0 && (
                <div style={{ textAlign: 'center', padding: '60px 40px', background: '#f8fafc', borderRadius: '12px', border: '2px dashed #e2e8f0', color: '#64748b' }}>
                    <AlertCircle size={32} style={{ color: '#94a3b8', marginBottom: '12px' }} />
                    <div style={{ fontWeight: '600', fontSize: '15px' }}>No keyword auto reply rules configured yet.</div>
                    <div style={{ fontSize: '13px', marginTop: '4px' }}>Click "+ Add Auto Reply" to get started.</div>
                </div>
            )}

            {!loading && rules.length > 0 && (
                <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '14px' }}>
                        <thead>
                            <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                <th style={{ padding: '14px 16px', color: '#475569', fontWeight: '600' }}>Keywords</th>
                                <th style={{ padding: '14px 16px', color: '#475569', fontWeight: '600' }}>Reply Text</th>
                                <th style={{ padding: '14px 16px', color: '#475569', fontWeight: '600' }}>Media</th>
                                <th style={{ padding: '14px 16px', color: '#475569', fontWeight: '600' }}>Interactive Buttons</th>
                                <th style={{ padding: '14px 16px', color: '#475569', fontWeight: '600' }}>Status</th>
                                <th style={{ padding: '14px 16px', color: '#475569', fontWeight: '600', textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rules.map(rule => (
                                <tr key={rule.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '14px 16px', color: '#0f172a', fontWeight: '600', maxWidth: '200px', wordBreak: 'break-word' }}>
                                        {rule.keywords}
                                    </td>
                                    <td style={{ padding: '14px 16px', color: '#334155', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {rule.reply_text}
                                    </td>
                                    <td style={{ padding: '14px 16px', color: '#64748b' }}>
                                        {rule.media ? <FileText size={18} style={{ color: '#3b82f6' }} title="Media Attached" /> : '-'}
                                    </td>
                                    <td style={{ padding: '14px 16px', color: '#0f172a' }}>
                                        {Array.isArray(rule.buttons) && rule.buttons.length > 0 ? (
                                            <span style={{ background: '#f1f5f9', padding: '4px 8px', borderRadius: '8px', fontSize: '12px', fontWeight: '600' }}>
                                                {rule.buttons.length} Buttons
                                            </span>
                                        ) : '-'}
                                    </td>
                                    <td style={{ padding: '14px 16px' }}>
                                        <button
                                            onClick={() => handleToggleStatus(rule)}
                                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}
                                        >
                                            {rule.is_active !== false ? (
                                                <ToggleRight size={28} style={{ color: '#10b981' }} />
                                            ) : (
                                                <ToggleLeft size={28} style={{ color: '#cbd5e1' }} />
                                            )}
                                        </button>
                                    </td>
                                    <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                                        <div style={{ display: 'inline-flex', gap: '8px' }}>
                                            <button
                                                onClick={() => handleOpenEditModal(rule)}
                                                style={{ border: '1px solid #e2e8f0', background: '#fff', padding: '6px', borderRadius: '8px', cursor: 'pointer', color: '#475569' }}
                                            >
                                                <Edit2 size={14} />
                                            </button>
                                            <button
                                                onClick={() => handleDeleteRule(rule.id)}
                                                style={{ border: '1px solid #fee2e2', background: '#fff', padding: '6px', borderRadius: '8px', cursor: 'pointer', color: '#ef4444' }}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* CREATE / EDIT MODAL */}
            {showModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'rgba(15, 23, 42, 0.45)',
                    backdropFilter: 'blur(4px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        background: '#fff',
                        width: '100%',
                        maxWidth: '520px',
                        borderRadius: '20px',
                        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                        padding: '24px',
                        position: 'relative',
                        maxHeight: '90vh',
                        overflowY: 'auto'
                    }}>
                        <button
                            onClick={() => setShowModal(false)}
                            style={{ position: 'absolute', top: '20px', right: '20px', background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer' }}
                        >
                            <X size={20} />
                        </button>

                        <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '700', color: '#0f172a' }}>
                            {modalMode === 'create' ? 'Create Auto Reply Rule' : 'Edit Auto Reply Rule'}
                        </h3>

                        <form onSubmit={handleSaveRule} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Trigger Keywords *</label>
                                <input
                                    type="text"
                                    placeholder="Jd, JD, Job desc"
                                    value={keywords}
                                    onChange={(e) => setKeywords(e.target.value)}
                                    style={{ padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' }}
                                />
                                <span style={{ fontSize: '11px', color: '#64748b' }}>Enter one or more trigger words separated by commas.</span>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Reply Message Content *</label>
                                <textarea
                                    rows="4"
                                    placeholder="Type the message that should be automatically sent back..."
                                    value={replyText}
                                    onChange={(e) => setReplyText(e.target.value)}
                                    style={{ padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none', resize: 'vertical', fontFamily: 'inherit' }}
                                />
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Attach Media File (Optional)</label>
                                <input
                                    type="file"
                                    onChange={(e) => setSelectedFile(e.target.files[0])}
                                    style={{ fontSize: '13px', color: '#64748b' }}
                                />
                                {existingMediaUrl && !selectedFile && (
                                    <span style={{ fontSize: '12px', color: '#3b82f6', marginTop: '4px' }}>
                                        Current: {existingMediaUrl.split('/').pop()}
                                    </span>
                                )}
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <label style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>Interactive Buttons / List Options (Up to 10)</label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {buttons.map((btn, index) => (
                                        <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                            <input
                                                type="text"
                                                placeholder={`Button ${index + 1} Text (max 20 chars)`}
                                                maxLength="20"
                                                value={btn}
                                                onChange={(e) => handleButtonChange(index, e.target.value)}
                                                style={{ flexGrow: 1, padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none' }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveButton(index)}
                                                style={{ background: 'transparent', border: 'none', color: '#ef4444', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={handleAddButton}
                                        style={{ alignSelf: 'flex-start', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: '8px', padding: '6px 12px', fontSize: '13px', color: '#475569', fontWeight: '600', cursor: 'pointer' }}
                                    >
                                        + Add Reply Button
                                    </button>
                                </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '10px' }}>
                                <input
                                    type="checkbox"
                                    id="isActive"
                                    checked={isActive}
                                    onChange={(e) => setIsActive(e.target.checked)}
                                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                                />
                                <label htmlFor="isActive" style={{ fontSize: '14px', fontWeight: '600', color: '#334155', cursor: 'pointer' }}>
                                    Enable this auto-reply rule
                                </label>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '16px' }}>
                                <button
                                    type="button"
                                    onClick={() => setShowModal(false)}
                                    style={{ background: '#fff', border: '1px solid #cbd5e1', borderRadius: '10px', padding: '10px 18px', fontSize: '14px', fontWeight: '600', color: '#475569', cursor: 'pointer' }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    style={{ background: 'var(--brand-purple, #7c3aed)', border: 'none', borderRadius: '10px', padding: '10px 20px', fontSize: '14px', fontWeight: '600', color: '#fff', cursor: 'pointer' }}
                                >
                                    Save Rule
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AutoReplyTab;
